import React, { useEffect, useState } from 'react';
import { CH_TYPE } from '../../styles/appUi';
import {
  Drawer, Box, Typography, IconButton, TextField, ToggleButton, ToggleButtonGroup, Button,
  Stack, Chip, Tooltip, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import toast from 'react-hot-toast';
import { useAppTheme } from '../../hooks/useAppTheme';
import { tradingService } from '../../services/trading.service';
import SymbolAutocomplete, { SymbolOption } from '../common/SymbolAutocomplete';
import { useConfirm } from '../common/ConfirmDialog';

export interface QuickTradePrefill {
  symbol?: string;
  exchange?: string;
  side?: 'BUY' | 'SHORT';
  quantity?: number;
  entryPrice?: number;
  stopLoss?: number;
  target?: number;
  source?: string;
}

const PREFILL_EVENT = 'quick-trade:prefill';

/** Fire this from anywhere to open the Quick-Trade panel pre-filled. */
export const openQuickTrade = (prefill: QuickTradePrefill) => {
  window.dispatchEvent(new CustomEvent(PREFILL_EVENT, { detail: prefill }));
};

const QuickTradePanel: React.FC = () => {
  const { C } = useAppTheme();
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [symbol, setSymbol] = useState('');
  const [exchange, setExchange] = useState('');
  const [side, setSide] = useState<'BUY' | 'SHORT'>('BUY');
  const [quantity, setQuantity] = useState('');
  const [entry, setEntry] = useState('');
  const [sl, setSl] = useState('');
  const [target, setTarget] = useState('');
  const [orderType, setOrderType] = useState<'MARKET' | 'LIMIT'>('LIMIT');
  const [error, setError] = useState<string | null>(null);

  // External prefill listener (alerts → quick-trade)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<QuickTradePrefill>).detail || {};
      if (detail.symbol) setSymbol(detail.symbol);
      if (detail.exchange) setExchange(detail.exchange);
      if (detail.side) setSide(detail.side);
      if (detail.quantity != null) setQuantity(String(detail.quantity));
      if (detail.entryPrice != null) setEntry(String(detail.entryPrice));
      if (detail.stopLoss != null) setSl(String(detail.stopLoss));
      if (detail.target != null) setTarget(String(detail.target));
      setOpen(true);
    };
    window.addEventListener(PREFILL_EVENT, handler);
    return () => window.removeEventListener(PREFILL_EVENT, handler);
  }, []);

  const reset = () => {
    setSymbol(''); setExchange(''); setSide('BUY');
    setQuantity(''); setEntry(''); setSl(''); setTarget('');
    setOrderType('LIMIT'); setError(null);
  };

  const validate = (): string | null => {
    if (!symbol || !exchange) return 'Pick a symbol from the dropdown.';
    const q = parseInt(quantity, 10);
    if (!Number.isFinite(q) || q <= 0) return 'Quantity must be a positive integer.';
    const e = parseFloat(entry);
    if (orderType === 'LIMIT' && (!Number.isFinite(e) || e <= 0)) return 'Entry price required for LIMIT orders.';
    if (sl) {
      const s = parseFloat(sl);
      if (!Number.isFinite(s) || s <= 0) return 'Stop loss must be a positive number.';
      if (orderType === 'LIMIT' && Number.isFinite(e)) {
        if (side === 'BUY' && s >= e) return 'For a BUY, stop loss must be below entry.';
        if (side === 'SHORT' && s <= e) return 'For a SHORT, stop loss must be above entry.';
      }
    }
    if (target) {
      const t = parseFloat(target);
      if (!Number.isFinite(t) || t <= 0) return 'Target must be a positive number.';
    }
    return null;
  };

  const submit = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setError(null);
    const ok = await confirm({
      title: `${side} ${quantity} × ${symbol}?`,
      body: `${orderType} order will be placed on ${exchange}. ${sl ? `SL ${sl}. ` : ''}${target ? `Target ${target}.` : ''}`.trim(),
      confirmLabel: side === 'BUY' ? 'Place BUY' : 'Place SHORT',
      confirmColor: side === 'BUY' ? 'success' : 'error',
    });
    if (!ok) return;
    setSubmitting(true);
    try {
      await tradingService.createManualTrade({
        symbol,
        exchange,
        side,
        quantity: parseInt(quantity, 10),
        entryPrice: parseFloat(entry || '0'),
        stopLoss: sl ? parseFloat(sl) : undefined,
        target: target ? parseFloat(target) : undefined,
        tradeType: orderType,
      });
      toast.success(`${side} ${quantity} × ${symbol} submitted`);
      reset();
      setOpen(false);
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message || err?.message || 'Failed to place order';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Tooltip arrow title="Quick-Trade — symbol, side, qty, SL, target, execute">
        <Box
          onClick={() => setOpen(true)}
          sx={{
            position: 'fixed',
            right: 16,
            bottom: 16,
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1,
            borderRadius: 4,
            bgcolor: C.accent,
            color: C.onSolid,
            boxShadow: 4,
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: CH_TYPE.body,
            letterSpacing: 0.3,
            transition: 'transform 0.12s ease, box-shadow 0.12s ease',
            '&:hover': { transform: 'translateY(-1px)', boxShadow: 6 },
          }}
        >
          <FlashOnIcon sx={{ fontSize: 18 }} />
          Quick Trade
        </Box>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 380 }, p: 2 } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <FlashOnIcon sx={{ color: C.accent }} />
            Quick Trade
          </Typography>
          <IconButton size="small" onClick={() => setOpen(false)}><CloseIcon /></IconButton>
        </Box>

        <Stack spacing={1.5}>
          <SymbolAutocomplete
            value={symbol}
            onChange={(opt: SymbolOption | null) => {
              setSymbol(opt?.symbol || '');
              setExchange(opt?.exchange || '');
            }}
            label="Symbol"
          />

          <ToggleButtonGroup
            value={side}
            exclusive
            onChange={(_, v) => v && setSide(v)}
            size="small"
            fullWidth
          >
            <ToggleButton value="BUY" sx={{ fontWeight: 700, color: side === 'BUY' ? C.onSolid : undefined, bgcolor: side === 'BUY' ? `${C.bull} !important` : undefined }}>
              BUY
            </ToggleButton>
            <ToggleButton value="SHORT" sx={{ fontWeight: 700, color: side === 'SHORT' ? C.onSolid : undefined, bgcolor: side === 'SHORT' ? `${C.bear} !important` : undefined }}>
              SHORT
            </ToggleButton>
          </ToggleButtonGroup>

          <ToggleButtonGroup
            value={orderType}
            exclusive
            onChange={(_, v) => v && setOrderType(v)}
            size="small"
            fullWidth
          >
            <ToggleButton value="LIMIT" sx={{ fontWeight: 600, fontSize: CH_TYPE.body }}>LIMIT</ToggleButton>
            <ToggleButton value="MARKET" sx={{ fontWeight: 600, fontSize: CH_TYPE.body }}>MARKET</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            label="Quantity"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value.replace(/[^0-9]/g, ''))}
            size="small"
            fullWidth
            placeholder="lots / units"
          />
          <TextField
            label={orderType === 'MARKET' ? 'Entry (informational)' : 'Entry'}
            value={entry}
            onChange={(e) => setEntry(e.target.value.replace(/[^0-9.]/g, ''))}
            size="small"
            fullWidth
          />
          <Stack direction="row" spacing={1}>
            <TextField
              label="Stop Loss"
              value={sl}
              onChange={(e) => setSl(e.target.value.replace(/[^0-9.]/g, ''))}
              size="small"
              fullWidth
            />
            <TextField
              label="Target"
              value={target}
              onChange={(e) => setTarget(e.target.value.replace(/[^0-9.]/g, ''))}
              size="small"
              fullWidth
            />
          </Stack>

          {sl && entry && (() => {
            const e = parseFloat(entry); const s = parseFloat(sl); const q = parseInt(quantity, 10) || 0;
            if (!Number.isFinite(e) || !Number.isFinite(s) || q <= 0) return null;
            const risk = Math.abs((e - s) * q);
            return (
              <Chip
                size="small"
                label={`Risk per trade ≈ ₹${risk.toFixed(2)}`}
                sx={{ alignSelf: 'flex-start', bgcolor: C.warnMuted, color: C.warn, fontWeight: 700 }}
              />
            );
          })()}

          {error && <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert>}

          <Stack direction="row" spacing={1}>
            <Button onClick={reset} color="inherit" sx={{ flex: 1 }}>Reset</Button>
            <Button
              onClick={submit}
              variant="contained"
              disabled={submitting}
              color={side === 'BUY' ? 'success' : 'error'}
              sx={{ flex: 2, fontWeight: 700 }}
            >
              {submitting ? 'Submitting…' : `Place ${side}`}
            </Button>
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ fontSize: CH_TYPE.small }}>
            Creates a manual trade via the standard order pipeline. Existing Settings (paper trade, order confirmation, kill switch) apply.
          </Typography>
        </Stack>
      </Drawer>
    </>
  );
};

export default QuickTradePanel;
